import type { Dtos } from "@inrev/common";
import { useFetchAdminBondIssuance } from "@portal/api/bond-request/admin";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { useMemo } from "react";

export type BondRequestNoticeId =
	| "review_uploaded_bond_form"
	| "review_uploaded_bond_form_for_agent_issue"
	| "fill_bond_form_for_electronic_issue"
	| "print_library_bond_form"
	| "print_uploaded_bond_form"
	| "upload_agent_issued_bond_form"
	| "finalize_agent_issued_bond_form"
	| "pending_indemnity_agreement"
	| "pending_agent_issue";

export type BondRequestNotice = {
	id: BondRequestNoticeId;
	type: "action" | "pending";
	text: string;
};

export const useAdminBondRequestNotices = (
	request: AdminBondRequest,
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined,
): BondRequestNotice[] => {
	const { bondIssuance } = useFetchAdminBondIssuance(request.suretyAccountId, request.id);
	const { status } = request;

	return useMemo(() => {
		const actions: BondRequestNotice[] = [];
		const pending: BondRequestNotice[] = [];
		const action = (id: BondRequestNoticeId, text: string) =>
			actions.push({ id, type: "action", text });

		if (status === "review" && bondForm?.source === "upload") {
			action("review_uploaded_bond_form", "Review uploaded bond form");
		}

		if (status === "pending" && bondIssuance) {
			if (bondIssuance.pendingBondFormReview && bondForm) {
				if (bondForm.issuanceMethod === "agent") {
					action(
						"review_uploaded_bond_form_for_agent_issue",
						"Review uploaded bond form for agent issue",
					);
				} else {
					action("review_uploaded_bond_form", "Review uploaded bond form");
				}
			}
			if (bondIssuance.pendingInRevIssue && bondForm) {
				if (bondForm.issuanceMethod === "electronic") {
					action(
						"fill_bond_form_for_electronic_issue",
						"Fill uploaded bond form, add digital seal and signatures, and send to the agent for electronic issue",
					);
				} else {
					if (bondForm.source === "library") {
						action(
							"print_library_bond_form",
							"Print pre-filled bond form, add physical seal and wet signatures, and send both electronic and physical copies to the agent",
						);
					}
					if (bondForm.source === "upload") {
						action(
							"print_uploaded_bond_form",
							"Print and fill uploaded bond form, add physical seal and wet signatures, and send both electronic and physical copies to the agent",
						);
					}
				}
			}
			if (bondIssuance.pendingAgentIssue) {
				action("upload_agent_issued_bond_form", "Upload a copy of the agent issued bond form");
			}
			if (bondIssuance.pendingAgentIssueAdminReview) {
				action("finalize_agent_issued_bond_form", "Review and finalize agent issued bond form");
			}
			if (bondIssuance.pendingIndemnityAgreement) {
				pending.push({
					id: "pending_indemnity_agreement",
					type: "pending",
					text: "Waiting for one or more indemnitors to sign the indemnity agreement",
				});
			}
			if (bondIssuance.pendingAgentIssue && !bondIssuance.pendingBondFormReview) {
				pending.push({ id: "pending_agent_issue", type: "pending", text: "Pending agent issue" });
			}
		}

		return [...actions, ...pending];
	}, [status, bondForm, bondIssuance]);
};
